import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Camera } from "lucide-react";
import { IMG } from "../lib/site";
import { Reveal, Eyebrow, EmberField, GoldButton, EASE } from "../components/motion";

export const WALL = [
  { img: IMG.heroTable, title: "The Belleville", sub: "Marine commission · Belleville, ON", aspect: "aspect-[4/5]", testId: "gallery-tile-belleville" },
  { img: IMG.twilight, title: "Cottage Evenings", sub: "The lakeside decks we design for · Muskoka", aspect: "aspect-[4/3]", testId: "gallery-tile-cottage" },
  { img: IMG.flame, title: "The Ember Bed", sub: "Volcanic lava on flamed black granite", aspect: "aspect-square", testId: "gallery-tile-ember" },
  { img: IMG.vineyard, title: "Winery Terraces", sub: "Hospitality settings · Niagara wine country", aspect: "aspect-[4/5]", testId: "gallery-tile-winery" },
  { img: IMG.branding, title: "Estate Crest Burning", sub: "Concept emblem · hand-finished into oak", aspect: "aspect-[4/5]", testId: "gallery-tile-crest" },
  { img: IMG.cellar, title: "Provenance", sub: "Bourbon rickhouse sourcing · Kentucky", aspect: "aspect-[4/3]", testId: "gallery-tile-provenance" },
  { img: IMG.estate, title: "Lakefront Estates", sub: "Architectural settings · dusk gatherings", aspect: "aspect-square", testId: "gallery-tile-estate" },
  { img: IMG.swatches, title: "The Stone Library", sub: "In the workshop · Ontario", aspect: "aspect-[4/3]", testId: "gallery-tile-stones" },
];

export default function Gallery() {
  const [active, setActive] = useState(null);

  return (
    <div className="relative overflow-hidden pb-28 pt-40">
      <EmberField count={16} />
      <div className="animate-ember-pulse absolute left-[12%] top-[15%] h-96 w-96 rounded-full bg-amber-600/10 blur-[120px]" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-10">
        <Reveal><Eyebrow>The Lookbook</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h1 className="mt-5 max-w-3xl font-serif text-4xl font-semibold leading-[1.08] tracking-tight text-stone-50 sm:text-5xl lg:text-6xl">
            Around the <span className="italic text-gold-gradient">fire.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-stone-400 sm:text-lg">
            Commissions in the wild, details from the workshop, and the properties we design for.
            New installations are added as pieces find their homes.
          </p>
        </Reveal>

        <div data-testid="gallery-wall" className="mt-16 columns-1 gap-5 sm:columns-2 lg:columns-3">
          {WALL.map((t, i) => (
            <Reveal key={t.testId} delay={(i % 3) * 0.1} className="mb-5 break-inside-avoid">
              <button
                type="button"
                data-testid={t.testId}
                onClick={() => setActive(t)}
                className="group relative block w-full overflow-hidden rounded-2xl border border-stone-800 text-left transition-[border-color,box-shadow] duration-300 hover:border-amber-500/45 hover:shadow-ember"
              >
                <img
                  src={t.img}
                  alt={t.title}
                  className={`${t.aspect} w-full object-cover transition-transform duration-[1400ms] group-hover:scale-105`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent opacity-90 transition-opacity duration-300 group-hover:opacity-100" />
                <span className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-amber-500/40 bg-black/60 opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100">
                  <Camera size={14} className="text-amber-400" />
                </span>
                <span className="absolute inset-x-0 bottom-0 block p-5">
                  <span className="block font-mono text-[10px] uppercase tracking-[0.26em] text-amber-400">{t.sub}</span>
                  <span className="mt-1 block font-serif text-xl text-stone-50">{t.title}</span>
                </span>
              </button>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-16 flex flex-wrap items-center justify-between gap-6 rounded-2xl border border-amber-900/30 bg-[#100d0b] p-8">
            <p className="max-w-md font-serif text-xl italic leading-relaxed text-stone-200">
              Have a property waiting for its gathering place?
            </p>
            <Link to="/configurator">
              <GoldButton data-testid="gallery-cta-atelier">Commission Your Piece <ArrowRight size={15} /></GoldButton>
            </Link>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-black/92 p-5 backdrop-blur-md sm:p-12"
          >
            <button
              type="button"
              data-testid="gallery-lightbox-close"
              onClick={() => setActive(null)}
              aria-label="Close"
              className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-full border border-amber-500/40 bg-black/60 text-stone-200 transition-colors duration-200 hover:text-amber-400"
            >
              <X size={20} />
            </button>
            <motion.figure
              initial={{ opacity: 0, scale: 0.94, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              transition={{ duration: 0.45, ease: EASE }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-full w-full max-w-3xl"
            >
              <div className="overflow-hidden rounded-2xl border border-amber-500/30 shadow-ember-lg">
                <img src={active.img} alt={active.title} className="max-h-[72vh] w-full object-cover" />
              </div>
              <figcaption className="mt-5 flex items-baseline justify-between gap-6">
                <span className="font-serif text-2xl italic text-stone-100">{active.title}</span>
                <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-amber-500/90">{active.sub}</span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
